import React, { useEffect, useMemo, useState } from "react";
import { DashboardStudioCoachCard } from "../Dashboard/DashboardStudioCoachCard";

/**
 * StudioStatsRibbon
 * Clean version — no emoji, no icons.
 * Shows kiln hours, total products, and clay used.
 * Auto-syncs from localStorage + live updates.
 */
export default function StudioStatsRibbon({
  isDark = false,
  batches: batchesProp,
  kilnLogs: kilnLogsProp,
  materials: materialsProp,
  showCoachCard = false,
  className = "",
}) {
  // ----- Safe JSON helper
  const ls = (k, fallback) => {
    try {
      const raw = localStorage.getItem(k);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  // ----- Local states
  const [lsBatches, setLsBatches] = useState(() => ls("mudoBatches", []));
  const [lsKiln, setLsKiln] = useState(() => ls("mudoKilnLogs", []));
  const [lsMaterials, setLsMats] = useState(() => ls("mudoMaterials", []));

  // ----- Listen to changes
  useEffect(() => {
    const refresh = () => {
      setLsBatches(ls("mudoBatches", []));
      setLsKiln(ls("mudoKilnLogs", []));
      setLsMats(ls("mudoMaterials", []));
    };
    window.addEventListener("storage", refresh);
    window.addEventListener("mudo:batches-updated", refresh);
    window.addEventListener("mudo:kiln-updated", refresh);
    window.addEventListener("mudo:materials-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("mudo:batches-updated", refresh);
      window.removeEventListener("mudo:kiln-updated", refresh);
      window.removeEventListener("mudo:materials-updated", refresh);
    };
  }, []);

  // Prefer props if passed
  const batches = Array.isArray(batchesProp)
    ? batchesProp
    : Array.isArray(lsBatches)
    ? lsBatches
    : [];
  const kilnLogs = Array.isArray(kilnLogsProp)
    ? kilnLogsProp
    : Array.isArray(lsKiln)
    ? lsKiln
    : [];
  const materials = Array.isArray(materialsProp)
    ? materialsProp
    : Array.isArray(lsMaterials)
    ? lsMaterials
    : [];

  // ----- Calculations -----
  const hoursBetween = (a, b) => {
    const s = new Date(a).getTime();
    const e = new Date(b).getTime();
    return Number.isFinite(s) && Number.isFinite(e) && e > s ? (e - s) / 36e5 : 0;
  };

  const totalKilnHours = useMemo(() => {
    return kilnLogs.reduce((sum, log) => {
      const d = Number(log?.durationHours);
      if (Number.isFinite(d) && d > 0) return sum + d;
      if (log?.start && log?.end) return sum + hoursBetween(log.start, log.end);
      return sum;
    }, 0);
  }, [kilnLogs]);

  const totalProducts = useMemo(() => {
    return batches.reduce((sum, b) => {
      if (Number.isFinite(Number(b?.unitsCompleted))) return sum + Number(b.unitsCompleted);
      if (Number.isFinite(Number(b?.qty))) return sum + Number(b.qty);
      if (Array.isArray(b?.items)) {
        return sum + b.items.reduce((s, it) => s + (Number(it?.qty) || 0), 0);
      }
      return sum;
    }, 0);
  }, [batches]);

  const totalClayKg = useMemo(() => {
    return materials.reduce((sum, m) => {
      const isClay =
        (m?.category && String(m.category).toLowerCase().includes("clay")) ||
        (m?.name && String(m.name).toLowerCase().includes("clay"));
      if (!isClay) return sum;
      const candidates = [
        m.usedKg,
        m.usageKg,
        m.consumedKg,
        m.kgUsed,
        m.quantityUsedKg,
        m.qtyUsedKg,
      ];
      for (const v of candidates) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) return sum + n;
      }
      return sum;
    }, 0);
  }, [materials]);

  // ----- Cards -----
  const statCards = [
    { key: "kiln", label: "Kiln hours", value: totalKilnHours, fmt: (n) => `${n.toFixed(1)} h` },
    { key: "products", label: "Products made", value: totalProducts, fmt: (n) => `${n}` },
    { key: "clay", label: "Clay used", value: totalClayKg, fmt: (n) => `${n.toFixed(1)} kg` },
  ];

  const ring = "inset 0 0 0 1px rgba(255,255,255,.14)";
  const ringLight = "inset 0 0 0 1px rgba(0,0,0,.12)";

  // ----- Render -----
  return (
    <div
      className={`rounded-xl p-2 md:p-3 flex gap-2 md:gap-3 overflow-x-auto no-scrollbar ${className}`}
      style={{
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.65)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: isDark ? ring : ringLight,
        border: isDark ? "1px solid rgba(255,255,255,.12)" : "1px solid rgba(0,0,0,.08)",
      }}
    >
      {statCards.map((c) => (
        <div
          key={c.key}
          className="min-w-[160px] md:min-w-[180px] rounded-xl px-4 py-2 flex flex-col justify-center"
          title={c.label}
          style={{
            background: isDark ? "rgba(0,0,0,.25)" : "rgba(255,255,255,.9)",
            border: isDark ? "1px solid rgba(255,255,255,.10)" : "1px solid rgba(0,0,0,.06)",
          }}
        >
          <div className="text-xs opacity-70">{c.label}</div>
          <div className="text-lg font-semibold">
            {Number.isFinite(c.value) ? c.fmt(c.value) : "--"}
          </div>
        </div>
      ))}
      {showCoachCard && (
        <div className="min-w-[200px] md:min-w-[220px] flex-1">
          <DashboardStudioCoachCard variant="compact" />
        </div>
      )}
    </div>
  );
}
